import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {AvatarStack} from '@primer/react'

interface AssigneeAvatar {
  login: string
  avatarUrl: string
}

interface AssigneeAvatarsProps {
  assignees: AssigneeAvatar[]
  /** Invoked with the assignee login when an avatar is clicked. When omitted the avatars are static. */
  onSelect?: (login: string) => void
  /** Class applied to the filter button so each row can preserve its own link styling. */
  filterLinkClassName?: string
  /** Right-align the stack (used in the default-density metadata column). */
  alignRight?: boolean
}

/**
 * Shared assignee avatar stack for dashboard list rows. Renders user hovercards
 * so issue and pull-request rows behave identically in the mixed list. Returns
 * `null` when there are no assignees.
 */
export function AssigneeAvatars({assignees, onSelect, filterLinkClassName, alignRight = false}: AssigneeAvatarsProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  if (assignees.length === 0) return null

  return (
    <AvatarStack alignRight={alignRight}>
      {assignees.map(assignee => {
        const hovercardAttributes = getHovercardAttributesForType('user', {login: assignee.login})

        return onSelect ? (
          <button
            key={assignee.login}
            type="button"
            className={filterLinkClassName}
            onClick={() => onSelect(assignee.login)}
            aria-label={`Filter by assignee ${assignee.login}`}
            {...hovercardAttributes}
          >
            <GitHubAvatar src={assignee.avatarUrl} size={20} alt="" />
          </button>
        ) : (
          <GitHubAvatar
            key={assignee.login}
            src={assignee.avatarUrl}
            size={20}
            alt={assignee.login}
            {...hovercardAttributes}
          />
        )
      })}
    </AvatarStack>
  )
}
